// KAN-661 R14/R36 — THIS MUST REMAIN THE FIRST IMPORT STATEMENT IN THIS FILE.
//
// ES module imports are hoisted and every imported module body evaluates, in
// source order, before the first statement below runs. A briefing-context token
// arriving as `#briefing-context=...` therefore has to be captured and stripped
// from the URL inside an imported module body, not by a call placed above
// `Sentry.init()` — by then `@sentry/react` has already evaluated. Moving this
// line down, or converting it to a call, silently reopens the leak.
//
// Proved by observation, not by this comment: mocked telemetry modules snapshot
// `location.hash` as they evaluate in
// `src/lib/__tests__/briefing-context-bootstrap.test.ts`.
import './lib/briefing-context-bootstrap'

import * as Sentry from '@sentry/react'
import { dropThirdPartyNoise } from './lib/sentry-filters'

Sentry.init({
  dsn: import.meta.env.VITE_SENTRY_DSN,
  environment: import.meta.env.MODE,
  release: __SENTRY_RELEASE__,
  enabled: !!import.meta.env.VITE_SENTRY_DSN,
  integrations: [Sentry.browserTracingIntegration()],
  tracesSampleRate: 0.1,
  // Third-party noise rules, composed. KAN-434: extension-injected
  // appendChild/JSON.stringify circular-structure errors, scoped by stack
  // shape rather than by message so a genuine first-party circular-JSON bug
  // still reports. KAN-646: the Outlook Safe Links scanner's non-Error
  // rejection, scoped by Sentry's synthetic rejection shape plus an anchored
  // payload. See lib/sentry-filters.ts for the evidence behind each.
  beforeSend: dropThirdPartyNoise,
})
Sentry.setTag('service', 'intelhub-web')
Sentry.setTag('component', 'web')

import { initAnalytics } from '@intelhub/analytics/client'

// R7: VITE_ prefix is mandatory — Vite exposes only VITE_* to the browser
// bundle. A bare POSTHOG_KEY here is invisible and the client silently no-ops.
initAnalytics({ key: import.meta.env.VITE_POSTHOG_KEY, role: 'anon' })

// KAN-433: the service worker is registered explicitly rather than by
// vite-plugin-pwa's injected shim, so the registration promise has a rejection
// handler. See lib/register-sw.ts for why.
import { initServiceWorkerRegistration } from './lib/register-sw'

initServiceWorkerRegistration()

import React from 'react'
import ReactDOM from 'react-dom/client'
import { BrowserRouter } from 'react-router-dom'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { ThemeProvider } from 'next-themes'
import { SentryErrorBoundary } from './components/SentryErrorBoundary'
import App from './App'
import './index.css'

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000, // 5 minutes
      refetchOnWindowFocus: false,
    },
  },
})

ReactDOM.createRoot(document.getElementById('root')!).render(
  <React.StrictMode>
    <SentryErrorBoundary>
      <BrowserRouter>
        <QueryClientProvider client={queryClient}>
          <ThemeProvider attribute="data-mode" defaultTheme="dark" enableSystem>
            <App />
          </ThemeProvider>
        </QueryClientProvider>
      </BrowserRouter>
    </SentryErrorBoundary>
  </React.StrictMode>,
)
